import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import ReceiptIcon from '@mui/icons-material/Receipt'
import Box from '@mui/joy/Box'
import Grid from '@mui/joy/Grid'
import Typography from '@mui/joy/Typography'
import { useTranslation } from 'react-i18next'

import { formatCzkAmount } from '@/components/checkout/order-summary/utils'
import ReservationDuration from '@/components/reservation-duration'
import type { CheckoutSuccessSummaryViewModel } from './summary-utils'

function Separator() {
    return (
        <Box sx={{
            height: '1px',
            backgroundColor: '#F3F4F6',
        }}/>
    )
}

function SummaryField({
    title,
    value,
}: {
    title: string
    value: string
}) {
    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
        }}>
            <Typography sx={{
                color: '#0B0B0B',
                fontSize: 15,
                fontWeight: 600,
            }}>
                {title}
            </Typography>
            <Typography sx={{
                fontSize: 15,
                color: 'primary.600',
                fontWeight: 400,
                minHeight: 22,
            }}>
                {value}
            </Typography>
        </Box>
    )
}

function DateTime({
    date,
    time,
}: {
    date: string
    time: string
}) {
    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'row',
        }}>
            <Typography sx={{
                fontSize: 15,
                color: '#0B0B0B',
                fontWeight: 400,
                lineHeight: '20px',
            }}>
                <Typography
                    component="b"
                    sx={{
                        fontWeight: 600,
                    }}
                >
                    {date},
                </Typography>{' '}
                {time}
            </Typography>
        </Box>
    )
}

export default function CheckoutSuccessReservationSummary({
    summary,
}: {
    summary: CheckoutSuccessSummaryViewModel
}) {
    const { t, i18n } = useTranslation()
    const locale = i18n.resolvedLanguage || i18n.language

    return (
        <Box>
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                mb: 1.5,
                justifyContent: {
                    xs: 'flex-start',
                    lg: 'center',
                },
            }}>
                <ReceiptIcon sx={{
                    color: '#0B0B0B',
                    fontSize: 20,
                    marginRight: 1,
                }} />
                <Typography
                    level="title-md"
                    component="h2"
                    sx={{
                        color: '#0B0B0B',
                        fontWeight: 600,
                    }}
                >
                    {t('success.summary.title')}
                </Typography>
            </Box>

            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: 'common.white',
                borderRadius: '10px',
                border: '1px solid var(--joy-palette-neutral-200)',
                boxShadow: '0 10px 30px rgba(15, 23, 42, 0.06)',
                overflow: 'hidden',
            }}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.5,
                        py: 1.5,
                        px: 2,
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: 16,
                            color: '#0B0B0B',
                            fontWeight: 600,
                            lineHeight: '20px',
                        }}
                    >
                        {summary.garageLine}
                    </Typography>
                    <Typography
                        component="div"
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.5,
                            fontSize: 13,
                            color: '#7A7A7A',
                            fontWeight: 400,
                            lineHeight: '17px',
                        }}
                    >
                        <Typography component="p">{summary.ownerName}</Typography>
                        <Typography component="p">{summary.ownerMeta}</Typography>
                    </Typography>
                </Box>

                <Separator />

                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    py: 1.5,
                    px: 2,
                }}>
                    <Box
                        sx={{
                            display: 'flex',
                            gap: '10px',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexWrap: 'wrap',
                        }}
                    >
                        <DateTime date={summary.beginsAtDate} time={summary.beginsAtTime} />
                        <ArrowForwardIcon sx={{ color: '#707070', fontSize: '18px' }} />
                        <DateTime date={summary.endsAtDate} time={summary.endsAtTime} />
                    </Box>
                    <Typography
                        sx={{
                            display: 'flex',
                            gap: 0.5,
                            flexWrap: 'wrap',
                            justifyContent: 'center',
                            color: '#0B0B0B',
                            fontWeight: 400,
                            fontSize: 15,
                            lineHeight: '20px',
                        }}
                    >
                        <ReservationDuration
                            reservationDays={summary.reservationDays}
                            reservationHours={summary.reservationHours}
                        />
                    </Typography>
                </Box>

                <Separator />

                <Box sx={{
                    py: 1.5,
                    px: 2,
                    color: '#0B0B0B',
                    fontWeight: 400,
                    fontSize: 15,
                    lineHeight: '20px',
                }}>
                    <Box sx={{
                        display: 'flex',
                        gap: 2,
                        flexDirection: 'column',
                    }}>
                        {summary.floorWithNumber &&
                            <SummaryField
                                title={t('success.reservationSummary.floorWithNumber')}
                                value={summary.floorWithNumber}
                            />
                        }
                        <SummaryField
                            title={t('success.reservationSummary.name')}
                            value={summary.name}
                        />
                        <SummaryField
                            title={t('success.reservationSummary.email')}
                            value={summary.email}
                        />
                        <Grid container spacing={2}>
                            <Grid xs={6}>
                                <SummaryField
                                    title={t('success.reservationSummary.phoneNumber')}
                                    value={summary.phoneNumber}
                                />
                            </Grid>
                            {summary.phoneNumber2 &&
                                <Grid xs={6}>
                                    <SummaryField
                                        title={t('success.reservationSummary.phoneNumber2')}
                                        value={summary.phoneNumber2}
                                    />
                                </Grid>
                            }
                            <Grid xs={6}>
                                <SummaryField
                                    title={t('success.reservationSummary.numberPlate')}
                                    value={summary.numberPlate}
                                />
                            </Grid>
                            <Grid xs={6}>
                                <SummaryField
                                    title={t('success.reservationSummary.carColor')}
                                    value={summary.carColor}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </Box>

                <Separator />

                <Box sx={{
                    py: 1.5,
                    px: 2,
                    display: 'flex',
                    justifyContent: 'end',
                }}>
                    <Box sx={{
                        display: 'flex',
                        gap: 1,
                        flexWrap: 'wrap',
                        justifyContent: 'flex-end',
                    }}>
                        <Typography
                            component="span"
                            sx={{
                                fontWeight: 400,
                                fontSize: 16,
                                color: '#0B0B0B',
                            }}
                        >
                            {t('success.reservationSummary.totalPrice')}
                        </Typography>
                        <Typography
                            component="span"
                            sx={{
                                fontWeight: 600,
                                fontSize: 17,
                                color: '#0B0B0B',
                            }}
                        >
                            {formatCzkAmount(summary.amount, locale)}{' '}
                            {summary.withVat ? (
                                <Typography
                                    component="span"
                                    sx={{
                                        fontSize: 14,
                                    }}
                                >
                                    {summary.withVat}
                                </Typography>
                            ) : null}
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </Box>
    )
}
